// Persisted selection state for CAVE conflict resolution.
//
// Conflict options are recomputed live from `formData.files[*].idpArtifacts`;
// only the user's decision is persisted so it survives a revisit:
//
//   RESOLUTIONS_KEY  view:caveResolutions[groupKey][fieldId]
//     'form'              — user kept the value they entered
//     <normalized value>  — user chose a scanned value (normalized so the
//                           selection survives file reorder/delete)
//
//   ORIGINALS_KEY    view:caveOriginals[groupKey][fieldId]
//     The user's entered value, captured when they pick a scanned option.
//     Write-through overwrites the form field with the chosen value, so the
//     original is what lets us show "entered by you", revert, and compare the
//     conflict against the user's value rather than the value we wrote.
//
// Both keys use the `view:` prefix so `transformForSubmit` drops them from the
// submitted payload; the real form field always holds the resolved value.

export const RESOLUTIONS_KEY = 'view:caveResolutions';
export const ORIGINALS_KEY = 'view:caveOriginals';

const hasArtifactPayload = file =>
  Object.values(file?.idpArtifacts ?? {}).some(
    entries => Array.isArray(entries) && entries.length > 0,
  );

export const getActiveTrackingKeys = files =>
  new Set(
    (files ?? [])
      .filter(file => file?.idpTrackingKey && hasArtifactPayload(file))
      .map(file => file.idpTrackingKey),
  );

// Signature used by the processing hook to re-run only when the set of files
// carrying artifact data changes (not on every status update).
export const getActiveTrackingKeySignature = files =>
  JSON.stringify(
    Array.from(getActiveTrackingKeys(files)).sort((a, b) => a.localeCompare(b)),
  );

// Value to compare scanned options against. While a scanned selection is
// active (the field still holds it and the original was captured), use the
// captured original so the conflict stays visible; otherwise use the live
// field value (covers no selection, a kept value, and manual edits).
export const getTypedValue = (formData, fieldDef, groupKey) => {
  const fieldValue = fieldDef.getFormValue(formData);
  const resolution = formData?.[RESOLUTIONS_KEY]?.[groupKey]?.[fieldDef.id];
  const original = formData?.[ORIGINALS_KEY]?.[groupKey]?.[fieldDef.id];
  if (
    resolution != null &&
    resolution !== 'form' &&
    original != null &&
    fieldDef.normalize(fieldValue) === resolution
  ) {
    return original;
  }
  return fieldValue;
};

// The radio that should render checked: the scanned option matching the stored
// resolution, but only while the field still holds it (a later manual edit
// falls back to 'form').
export const getActiveResolution = (formData, fieldDef, groupKey) => {
  const resolution = formData?.[RESOLUTIONS_KEY]?.[groupKey]?.[fieldDef.id];
  if (
    resolution != null &&
    resolution !== 'form' &&
    fieldDef.normalize(fieldDef.getFormValue(formData)) === resolution
  ) {
    return resolution;
  }
  return 'form';
};

// Returns true if any scanned artifact still reports `normValue` for this field.
const scannedValueStillExists = (fieldDef, files, normValue) =>
  fieldDef.artifacts.some(artifact =>
    (files ?? []).some(file =>
      (file.idpArtifacts?.[artifact.artifactKey] ?? []).some(entry => {
        const value = artifact.getArtifactValue(entry);
        return value != null && fieldDef.normalize(value) === normValue;
      }),
    ),
  );

// When the file set changes, revert any active scanned selection whose source
// value is gone (its file was removed) to the user's original, and clear
// markers left stale by a manual edit. Returns new formData, or null if
// nothing changed.
export const revertRemovedSelections = (formData, groups) => {
  const files = formData.files ?? [];
  let nextData = formData;
  let changed = false;
  const nextResolutions = { ...(formData[RESOLUTIONS_KEY] ?? {}) };
  const nextOriginals = { ...(formData[ORIGINALS_KEY] ?? {}) };

  Object.entries(groups).forEach(([groupKey, fieldGroup]) => {
    const resolutions = { ...(nextResolutions[groupKey] ?? {}) };
    const originals = { ...(nextOriginals[groupKey] ?? {}) };
    let groupChanged = false;

    Object.keys(resolutions).forEach(fieldId => {
      const resolution = resolutions[fieldId];
      if (resolution === 'form') return;

      const fieldDef = fieldGroup.find(f => f.id === fieldId);
      if (!fieldDef) {
        delete resolutions[fieldId];
        delete originals[fieldId];
        groupChanged = true;
        return;
      }

      const fieldNorm = fieldDef.normalize(fieldDef.getFormValue(nextData));
      const selectionActive = fieldNorm === resolution;
      if (!selectionActive) {
        // Field was manually edited after the selection — marker is stale.
        delete resolutions[fieldId];
        delete originals[fieldId];
        groupChanged = true;
        return;
      }

      if (!scannedValueStillExists(fieldDef, files, resolution)) {
        const original = originals[fieldId];
        if (original !== undefined) {
          nextData = fieldDef.applyToForm(nextData, original);
        }
        delete resolutions[fieldId];
        delete originals[fieldId];
        groupChanged = true;
      }
    });

    if (groupChanged) {
      changed = true;
      if (Object.keys(resolutions).length) {
        nextResolutions[groupKey] = resolutions;
      } else {
        delete nextResolutions[groupKey];
      }
      if (Object.keys(originals).length) {
        nextOriginals[groupKey] = originals;
      } else {
        delete nextOriginals[groupKey];
      }
    }
  });

  if (!changed) return null;

  return {
    ...nextData,
    [RESOLUTIONS_KEY]: nextResolutions,
    [ORIGINALS_KEY]: nextOriginals,
  };
};
